/**
 * Currency Module - Display Formatting
 *
 * Functions that convert raw API cents into formatted display strings.
 * These are the primary exports most components will use.
 *
 * Design decisions:
 * - Smart decimals ON by default: $50 not $50.00, but $50.50 keeps decimals
 * - Null/NaN safe: returns $0 equivalent instead of crashing
 * - Multi-currency: respects currency code for symbol and locale
 * - Negative amounts: formats correctly for refunds (-$25)
 */

import type { FormatOptions } from './types';
import { ZERO_DECIMAL_CURRENCIES } from './types';

/**
 * Format an amount in cents as a display string.
 * This is the primary formatting function -- use for all API monetary values.
 *
 * @param cents - Amount in cents from the API (e.g., 2500 = $25.00)
 * @param options - Formatting options (currency, locale, smartDecimals)
 * @returns Formatted currency string
 *
 * @example
 * formatCents(2500)                              // '$25'
 * formatCents(2550)                              // '$25.50'
 * formatCents(2500, { currency: 'GBP' })         // '£25'
 * formatCents(null)                              // '$0'
 * formatCents(-2500)                             // '-$25'
 * formatCents(2500, { smartDecimals: false })     // '$25.00'
 */
export function formatCents(
  cents: number | null | undefined,
  options?: FormatOptions
): string {
  const safeValue = toSafeNumber(cents);
  const currency = (options?.currency ?? 'USD').toUpperCase();
  const locale = options?.locale ?? 'en-US';
  const smartDecimals = options?.smartDecimals ?? true;

  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(currency);
  const units = isZeroDecimal ? safeValue : safeValue / 100;

  return formatWithIntl(units, currency, locale, smartDecimals);
}

/**
 * Convert cents to compact display parts for animated counters.
 * Use when you need { value, suffix } for count-up animations (e.g. SocialProofBar).
 * API amounts are in cents -- this handles conversion and K/M notation.
 *
 * @param cents - Amount in cents from the API (e.g., 20000 = $200)
 * @param currency - ISO 4217 currency code (default: 'USD')
 * @returns { value, suffix } for display as `prefix + value + suffix`
 *
 * @example
 * centsToCompactParts(20000)      // { value: 200, suffix: '' }
 * centsToCompactParts(250000)    // { value: 2.5, suffix: 'K' }
 * centsToCompactParts(1200000)   // { value: 12, suffix: 'K' }
 * centsToCompactParts(150000000) // { value: 1.5, suffix: 'M' }
 */
export function centsToCompactParts(
  cents: number | null | undefined,
  currency: string = 'USD'
): { value: number; suffix: string } {
  const safeValue = toSafeNumber(cents);
  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase());
  const units = isZeroDecimal ? safeValue : safeValue / 100;

  if (units >= 1_000_000) {
    return { value: Math.round((units / 1_000_000) * 10) / 10, suffix: 'M' };
  }
  if (units >= 1_000) {
    return { value: Math.round((units / 1_000) * 10) / 10, suffix: 'K' };
  }
  return { value: Math.round(units), suffix: '' };
}

/**
 * Format an amount in cents using compact notation (K, M, B).
 * Useful for dashboard stats and summary cards.
 *
 * @param cents - Amount in cents from the API
 * @param options - Formatting options
 * @returns Compact formatted string
 *
 * @example
 * formatCentsCompact(250000)     // '$2.5K'
 * formatCentsCompact(1200000)    // '$12K'
 * formatCentsCompact(150000000)  // '$1.5M'
 */
export function formatCentsCompact(
  cents: number | null | undefined,
  options?: FormatOptions
): string {
  const safeValue = toSafeNumber(cents);
  const currency = (options?.currency ?? 'USD').toUpperCase();
  const locale = options?.locale ?? 'en-US';

  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(currency);
  const units = isZeroDecimal ? safeValue : safeValue / 100;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    compactDisplay: 'short',
    maximumSignificantDigits: 3,
  }).format(units);
}

/**
 * Format an amount already in major currency units (dollars/pounds).
 * Use when the value has already been converted from cents, or for
 * values that arrive in dollars (e.g., user form input).
 *
 * Replaces the deprecated formatCurrency() and formatDollarAmount() in lib/utils.ts.
 *
 * @param units - Amount in major units (e.g., 25.00 = $25.00)
 * @param options - Formatting options
 * @returns Formatted currency string
 *
 * @example
 * formatUnits(25)                              // '$25'
 * formatUnits(25.50)                           // '$25.50'
 * formatUnits(25, { currency: 'EUR' })         // '€25'
 * formatUnits(25, { smartDecimals: false })     // '$25.00'
 */
export function formatUnits(
  units: number | null | undefined,
  options?: FormatOptions
): string {
  const safeValue = toSafeNumber(units);
  const currency = (options?.currency ?? 'USD').toUpperCase();
  const locale = options?.locale ?? 'en-US';
  const smartDecimals = options?.smartDecimals ?? true;

  return formatWithIntl(safeValue, currency, locale, smartDecimals);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Coerce a potentially null/undefined/NaN value to a safe number.
 * Returns 0 for any non-finite input.
 */
function toSafeNumber(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value)) return 0;
  return value;
}

/**
 * Format a numeric value using Intl.NumberFormat with smart decimal logic.
 * When smartDecimals is true, whole amounts omit the fractional part.
 */
function formatWithIntl(
  value: number,
  currency: string,
  locale: string,
  smartDecimals: boolean
): string {
  const isWholeAmount = value % 1 === 0;
  const minDecimals = smartDecimals && isWholeAmount ? 0 : 2;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: 2,
  }).format(value);
}
